<template>
  <div v-if="getIsProjectLoaded" class="list">
    <vddl-list
      class="panel__body--list"
      :class="{ dragover: dragOver }"
      :list="rootContainedAssets"
      :drop="onDrop"
      :external-sources="true"
      :allowed-types="allowedTypes"
      :disable-if="disable || !canEdit"
    >
      <asset-list-item
        v-for="(item, index) in rootContainedAssets"
        :id="item.id"
        :key="item.id"
        v-model:name="item.name"
        :list="rootContainedAssets"
        :index="index"
        :container-id="getRootContainer.id"
      />
    </vddl-list>

    <div v-if="canEdit">
      <div class="create_new">
        <div class="asset" @click="onClickCreate">
          <div class="icon">
            <svg-icon
              fill="#737575"
              :width="'14'"
              :height="'14'"
              icon="app-icons/create"
            />
          </div>
          <span>Upload asset</span>
        </div>
        <div class="folder" @click="onClickCreateFolder">
          <svg-icon
            fill="#bcbcbc"
            :width="'13'"
            :height="'13'"
            icon="app-icons/folder-add"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import Vddl from 'vddl';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, EVENT, DRAG_TYPE, DRAG_LIST } from '@/constants.js';
import assetTypes from '../../vuex/project/types/assets-types.js';
import SvgIcon from '../all/SvgIcon.vue';
import AssetListItem from './AssetListItem.vue';

export default {
  components: {
    'asset-list-item': AssetListItem,
    SvgIcon,
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      disable: false,
      containerId: 'asset_0',
      collapsedItem: null,
      allowedTypes: [DRAG_TYPE.ASSET, DRAG_TYPE.CONTAINER],
      dragOver: false,
    };
  },

  computed: {
    ...mapGetters('project/data/assets', [
      'getContainedNodes',
      'getRootContainer',
    ]),
    ...mapGetters('project/config/components', ['getSelections']),
    ...mapGetters('application/drag', ['getIsEmpty']),
    ...mapGetters('application/appStates', ['getIsProjectLoaded']),

    rootContainedAssets() {
      if (!this.getRootContainer) return [];

      return this.getContainedNodes(
        this.getRootContainer.id,
        'assets',
        'children'
      );
    },

    selectedId() {
      return this.getSelections.length === 0 ? null : this.getSelections[0];
    },
  },

  watch: {
    getIsEmpty(newVal, oldVal) {
      if (newVal) {
        this.dragOver = false;
      }
    },
  },

  mounted() {
    $(this.$el).on('dragenter', event => {
      this.onDragEnter(event);
    });
    $(this.$el).on('dragleave', event => {
      this.onDragLeave(event);
    });
  },

  unmounted() {
    $(this.$el).off('dragenter');
    $(this.$el).off('dragleave');
  },

  methods: {
    ...mapActions('project/config/assets', [
      'setOpen',
      'setCollapsed',
      'select',
    ]),
    ...mapActions('application/drag', ['setTarget']),
    ...mapActions('project/data/assets', ['createAsset']),

    /**
     * Event handler for vddl drop event. This is triggered before the item's move event.
     */
    onDrop(draggable) {
      console.log('OnDrop on Asset List');
      this.setTarget({
        value: {
          type: DRAG_LIST.ASSETS,
          id: this.item === undefined ? this.getRootContainer.id : this.item.id,
          index: draggable.index,
        },
      });
    },

    /**
     * Event handler for dragenter event. Something is being dragged over the list.
     */
    onDragEnter(event) {
      this.dragOver = true;
    },

    /**
     * Event handler for dragleave event. Something leaves the list.
     */
    onDragLeave(event) {
      const rect = event.currentTarget.getBoundingClientRect();

      // If the actual coordinates are outside the list area (the drag leave should ignore children)
      if (
        event.clientY <= rect.top ||
        event.clientY >= rect.bottom ||
        event.clientX <= rect.left ||
        event.clientX >= rect.right
      ) {
        this.dragOver = false;
      }
    },

    /**
     * Event handler - click on Upload Asset button
     */
    onClickCreate() {
      console.log('User clicked on new asset');
      emitter.emit(
        EVENT.OPEN_FILE_UPLOADER,
        this.getRootContainer.id,
        true,
        false
      );
    },

    /**
     * Event handler - click on New Folder button
     */
    onClickCreateFolder() {
      const tId = null;
      const tIndex = '-';
      this.createAsset({
        props: assetTypes.newContainer.data,
        tId,
        tIndex,
        tExpand: true,
        apply: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.create_new {
  cursor: pointer;
  display: flex;
  // padding-left: 6px;
  align-items: center;
  height: 30px;

  &:hover {
    background: #353c3e;
    .folder {
      opacity: 1;
    }
  }
  .asset {
    cursor: pointer;
    padding-left: 6px;
    align-items: center;
    display: flex;
    flex: 1;
    height: 100%;
    &:hover {
      span {
        opacity: 1;
      }
      :deep(.icon svg path) {
        fill: #bcbcbc !important;
      }
    }
  }
  .folder {
    opacity: 0;
    display: flex;
    padding: 6px 6px;
    margin: 0 10px 0 0;
    border-radius: 4px;
    &:hover {
      background: #2c3132;
    }
  }

  .icon {
    width: 22px;
    height: 22px;
    margin: 0 6px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  span {
    user-select: none;
    flex: 1 1 50%;
    display: block;
    color: #bcbcbc;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    font-size: 12px;
    opacity: 0.7;
  }
}
.list {
  height: 100%;
  overflow: auto;
}
</style>
